import React, { memo, useEffect } from 'react'
import times from 'lodash/times'
import InfoIcon from 'transfix-icons/build/InfoIcon'
import CircularProgress from 'transfix-ui/components/CircularProgress'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import Grid from 'transfix-ui/core/Grid'
import Skeleton from 'transfix-ui/core/Skeleton'
import Typography from 'transfix-ui/core/Typography'
import { makeStyles } from 'transfix-ui/core/styles'
import { useInView } from 'transfix-ui/hooks'
import { brandColors } from 'transfix-ui/theme/colors'
import ShipmentCard from '../../shipment/ShipmentCard'
import RecommendedShipmentsSectionEmpty from './components/RecommendedShipmentsSectionEmpty'
import { PER_PAGE, RECOMMENDED_SHIPMENTS_TEST_IDS, SECTION_TITLE, TOOLTIP_COPY } from './constants'
import { useRecommendedShipments } from './hooks'

const useRecommendedShipmentsSectionStyles = makeStyles(theme => ({
  title: {
    display: 'flex',
    marginBottom: theme.spacing(2),
  },
  infoTooltipWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(1),
  },
  loadMoreWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(4),
  },
  loadMoreSpinner: {
    color: brandColors.gold2,
    marginRight: theme.spacing(1),
  },
  indicator: {
    height: theme.spacing(2),
  },
  endOfList: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${theme.spacing(4)} 0`,
  },
}))

export interface IRecommendedShipmentsSectionProps {
  /**
   * @default "Loads for You"
   */
  title?: string
}

const RecommendedShipmentsSection = memo<IRecommendedShipmentsSectionProps>(
  ({ title = SECTION_TITLE }) => {
    const classes = useRecommendedShipmentsSectionStyles()
    const { inView: isListEndInView, ref } = useInView({ delay: 1000 })

    const {
      hasNextPage,
      hasRecommendations,
      isLoading,
      isLoadingMore,
      loadMoreHandler,
      recommendationSource,
      recommendations,
    } = useRecommendedShipments()

    /**
     * When the end of list is visible in viewport -- load another page of results.
     */
    useEffect(() => {
      if (isListEndInView) {
        loadMoreHandler()
      }
    }, [isListEndInView, loadMoreHandler])

    const showLoadingMore = hasNextPage || isLoadingMore
    const showEndOfListIndicator = hasNextPage && !isLoadingMore
    const showEndOfListText = !hasNextPage && hasRecommendations

    return (
      <div data-test='loads-for-you-section'>
        {isLoading ? (
          <div data-test={RECOMMENDED_SHIPMENTS_TEST_IDS.loadingSkeleton}>
            <div className={classes.title}>
              <Skeleton variant='rectangular' width='123px' height='27px' component='div' />
              <div className={classes.infoTooltipWrapper}>
                <Skeleton variant='rectangular' width='16px' height='16px' component='div' />
              </div>
            </div>
            <Grid container spacing={3}>
              {times(PER_PAGE).map(item => (
                <Grid key={item} item lg={4} xs={12}>
                  <Skeleton variant='rectangular' width='100%' height='196px' component='div' />
                </Grid>
              ))}
            </Grid>
          </div>
        ) : (
          <div>
            <div className={classes.title}>
              <Typography variant='h3'>{title}</Typography>
              <div className={classes.infoTooltipWrapper}>
                <Tooltip position='right' trigger={<InfoIcon size='medium' color='coolGray5' />}>
                  {TOOLTIP_COPY}
                </Tooltip>
              </div>
            </div>
            <Grid container spacing={3}>
              {!hasRecommendations && (
                <Grid item xs={12}>
                  <RecommendedShipmentsSectionEmpty />
                </Grid>
              )}
              {recommendations.map((recommendation, index) => {
                const page = Math.ceil((index + 1) / PER_PAGE)
                return (
                  recommendationSource && (
                    <Grid
                      key={`page-${page}-shipment-${recommendation.shipmentId}`}
                      item
                      lg={4}
                      md={6}
                      xs={12}>
                      <ShipmentCard
                        recommendation={recommendation}
                        recommendationSource={recommendationSource}
                        variant='book'
                        dataTest={RECOMMENDED_SHIPMENTS_TEST_IDS.shipmentCard}
                        indexInList={index + 1}
                      />
                    </Grid>
                  )
                )
              })}
            </Grid>
            {showLoadingMore && (
              <div className={classes.loadMoreWrapper}>
                <div className={classes.loadMoreSpinner}>
                  <CircularProgress size={20} color='inherit' />
                </div>
                <Typography variant='subtitle1'>Loading More...</Typography>
              </div>
            )}
            {showEndOfListIndicator && <div ref={ref} className={classes.indicator} />}
            {showEndOfListText && (
              <div className={classes.endOfList}>
                <Typography variant='subtitle1' color='textSecondary'>
                  You&apos;ve reached the end of the list.
                </Typography>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }
)

export default RecommendedShipmentsSection
